import { useState, useEffect } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleChangePassword } from "./ApiHandler/authFunctions";
import Axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

// Role config — colour + icon + label
const ROLE_CONFIG = {
    1: { label: "Admin",         icon: "bx bxs-crown",       gradient: "linear-gradient(135deg,#f59e0b,#d97706)", badge: "#f59e0b" },
    2: { label: "Mentor",        icon: "bx bxs-user-detail", gradient: "linear-gradient(135deg,#3b82f6,#1d4ed8)", badge: "#3b82f6" },
    3: { label: "State Officer", icon: "bx bxs-badge-check",  gradient: "linear-gradient(135deg,#10b981,#047857)", badge: "#10b981" },
};

const MyProfile = () => {
    const [profile, setProfile]   = useState(null);
    const [loading, setLoading]   = useState(true);
    const [passwords, setPasswords] = useState({ oldPassword: "", newPassword: "", confirmPassword: "" });
    const [showOld,  setShowOld]  = useState(false);
    const [showNew,  setShowNew]  = useState(false);
    const [showConf, setShowConf] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await Axios.get(`${API_URL}/users/fetchUserProfile`, {
                    headers: { Authorization: localStorage.getItem("token") }
                });
                if (res.data.status === "success") setProfile(res.data.data);
            } catch (err) {
                console.error("Error fetching profile:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPasswords(p => ({ ...p, [name]: value }));
    };

    const roleInfo = profile ? (ROLE_CONFIG[profile.role_id] || ROLE_CONFIG[2]) : null;

    // Avatar initials from username
    const initials = profile?.username
        ? profile.username.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)
        : "?";

    if (loading) return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
            <i className="bx bx-loader-circle bx-spin" style={{ fontSize: 40, color: "#667eea" }} />
        </div>
    );

    return (
        <div style={{ maxWidth: 820, margin: "0 auto", padding: "24px 16px" }}>
            <ToastContainer />

            {/* ── Profile Hero Card ─────────────────────────── */}
            <div style={{
                borderRadius: 20,
                overflow: "hidden",
                boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
                marginBottom: 24,
                background: "white"
            }}>
                {/* Gradient banner */}
                <div style={{
                    height: 110,
                    background: roleInfo?.gradient || "linear-gradient(135deg,#667eea,#764ba2)",
                    position: "relative"
                }}>
                    {/* Decorative circles */}
                    <div style={{ position:"absolute", top:-30, right:-30, width:160, height:160, borderRadius:"50%", background:"rgba(255,255,255,0.08)" }} />
                    <div style={{ position:"absolute", bottom:-40, left:40,  width:100, height:100, borderRadius:"50%", background:"rgba(255,255,255,0.06)" }} />
                </div>

                {/* Avatar overlapping banner */}
                <div style={{ padding: "0 32px 28px 32px", position: "relative" }}>
                    <div style={{
                        width: 90, height: 90, borderRadius: "50%",
                        background: roleInfo?.gradient || "linear-gradient(135deg,#667eea,#764ba2)",
                        border: "4px solid white",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 32, fontWeight: 800, color: "white",
                        position: "absolute", top: -45,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.2)"
                    }}>
                        {initials}
                    </div>

                    {/* Role badge top-right of card */}
                    <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 10 }}>
                        <span style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            background: roleInfo?.badge, color: "white",
                            borderRadius: 30, padding: "5px 14px",
                            fontSize: 13, fontWeight: 700,
                            boxShadow: `0 4px 12px ${roleInfo?.badge}55`
                        }}>
                            <i className={roleInfo?.icon} style={{ fontSize: 15 }} />
                            {roleInfo?.label}
                        </span>
                    </div>

                    {/* Name + email */}
                    <div style={{ marginTop: 32 }}>
                        <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: "#1a202c" }}>
                            {profile?.username || "—"}
                        </h2>
                        <p style={{ margin: "4px 0 0 0", fontSize: 14, color: "#718096" }}>
                            {profile?.email}
                        </p>
                    </div>

                    {/* Info grid */}
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                        gap: 16, marginTop: 24
                    }}>
                        {/* State card — only for non-admin */}
                        {profile?.role_id !== 1 && (
                            <InfoCard
                                icon="bx bx-map"
                                label="State"
                                value={profile?.state || "Not assigned"}
                                color="#667eea"
                            />
                        )}

                        {/* Assigned Lab — only for mentor */}
                        {profile?.role_id === 2 && (
                            <InfoCard
                                icon="bx bx-test-tube"
                                label="Assigned Lab"
                                value={profile?.assigned_lab_name || "Not assigned"}
                                sub={profile?.assigned_lab_type || ""}
                                color="#3b82f6"
                            />
                        )}

                        {/* School — only for mentor */}
                        {profile?.role_id === 2 && profile?.assigned_school_name && (
                            <InfoCard
                                icon="bx bxs-school"
                                label="School"
                                value={profile.assigned_school_name}
                                color="#10b981"
                            />
                        )}

                        {/* Admin scope */}
                        {profile?.role_id === 1 && (
                            <InfoCard
                                icon="bx bx-globe"
                                label="Scope"
                                value="All India"
                                color="#f59e0b"
                            />
                        )}

                        {/* State officer scope */}
                        {profile?.role_id === 3 && (
                            <InfoCard
                                icon="bx bx-globe"
                                label="Scope"
                                value={profile?.state ? `${profile.state} (State Level)` : "State Level"}
                                color="#10b981"
                            />
                        )}

                        {/* Phone if available */}
                        {profile?.phone && (
                            <InfoCard
                                icon="bx bx-phone"
                                label="Phone"
                                value={profile.phone}
                                color="#8b5cf6"
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* ── Reset Password Card ───────────────────────── */}
            <div style={{
                background: "white", borderRadius: 20,
                boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
                overflow: "hidden"
            }}>
                {/* Card header */}
                <div style={{
                    padding: "20px 28px",
                    borderBottom: "1px solid #f0f0f0",
                    display: "flex", alignItems: "center", gap: 12
                }}>
                    <div style={{
                        width: 40, height: 40, borderRadius: 10,
                        background: "linear-gradient(135deg,#667eea,#764ba2)",
                        display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                        <i className="bx bx-lock-alt" style={{ fontSize: 20, color: "white" }} />
                    </div>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#2d3748" }}>Reset Password</h3>
                        <p style={{ margin: 0, fontSize: 13, color: "#718096" }}>Enter your current password to set a new one</p>
                    </div>
                </div>

                <div style={{ padding: 28 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 16 }}>
                        <PasswordField
                            label="Old Password" name="oldPassword"
                            value={passwords.oldPassword} onChange={handleChange}
                            placeholder="Enter old password"
                            show={showOld} toggleShow={() => setShowOld(s => !s)}
                        />
                        <PasswordField
                            label="New Password" name="newPassword"
                            value={passwords.newPassword} onChange={handleChange}
                            placeholder="Enter new password"
                            show={showNew} toggleShow={() => setShowNew(s => !s)}
                        />
                        <PasswordField
                            label="Confirm Password" name="confirmPassword"
                            value={passwords.confirmPassword} onChange={handleChange}
                            placeholder="Confirm new password"
                            show={showConf} toggleShow={() => setShowConf(s => !s)}
                        />
                    </div>

                    {/* Strength indicator */}
                    {passwords.newPassword && (
                        <StrengthBar password={passwords.newPassword} />
                    )}

                    {/* Match indicator */}
                    {passwords.confirmPassword && (
                        <p style={{
                            marginTop: 8, fontSize: 13,
                            color: passwords.newPassword === passwords.confirmPassword ? "#10b981" : "#ef4444"
                        }}>
                            <i className={`bx ${passwords.newPassword === passwords.confirmPassword ? "bx-check-circle" : "bx-x-circle"}`} />
                            {" "}{passwords.newPassword === passwords.confirmPassword ? "Passwords match" : "Passwords do not match"}
                        </p>
                    )}

                    <button
                        onClick={(e) => handleChangePassword(
                            e,
                            passwords.oldPassword,
                            passwords.newPassword,
                            passwords.confirmPassword,
                            setPasswords
                        )}
                        style={{
                            marginTop: 20,
                            background: "linear-gradient(135deg,#667eea,#764ba2)",
                            color: "white", border: "none",
                            borderRadius: 10, padding: "12px 32px",
                            fontSize: 15, fontWeight: 700,
                            cursor: "pointer", width: "100%",
                            boxShadow: "0 4px 15px rgba(102,126,234,0.4)",
                            transition: "all 0.2s ease"
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(102,126,234,0.5)"; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 15px rgba(102,126,234,0.4)"; }}
                    >
                        <i className="bx bx-lock-open-alt" /> Update Password
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ── Small helper components ────────────────────────────────── */

const InfoCard = ({ icon, label, value, sub, color }) => (
    <div style={{
        background: `${color}0d`,
        border: `1.5px solid ${color}22`,
        borderRadius: 14, padding: "14px 16px",
        display: "flex", alignItems: "flex-start", gap: 12
    }}>
        <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: `${color}22`,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0
        }}>
            <i className={icon} style={{ fontSize: 18, color }} />
        </div>
        <div style={{ minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                {label}
            </p>
            <p style={{ margin: "3px 0 0 0", fontSize: 14, fontWeight: 700, color: "#1a202c", wordBreak: "break-word" }}>
                {value}
            </p>
            {sub && <p style={{ margin: "2px 0 0 0", fontSize: 12, color: "#718096" }}>{sub}</p>}
        </div>
    </div>
);

const PasswordField = ({ label, name, value, onChange, placeholder, show, toggleShow }) => (
    <div>
        <label style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 600, color: "#4a5568" }}>
            {label}
        </label>
        <div style={{ position: "relative" }}>
            <input
                type={show ? "text" : "password"}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                autoComplete="off"
                style={{
                    width: "100%", padding: "11px 42px 11px 14px",
                    border: "1.5px solid #e2e8f0",
                    borderRadius: 10, fontSize: 14,
                    outline: "none", boxSizing: "border-box",
                    transition: "border 0.2s"
                }}
                onFocus={e => { e.currentTarget.style.border = "1.5px solid #667eea"; }}
                onBlur={e => { e.currentTarget.style.border = "1.5px solid #e2e8f0"; }}
            />
            <button
                type="button"
                onClick={toggleShow}
                style={{
                    position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer",
                    color: "#9ca3af", fontSize: 18, padding: 0, display: "flex"
                }}
            >
                <i className={`bx ${show ? "bx-hide" : "bx-show"}`} />
            </button>
        </div>
    </div>
);

const StrengthBar = ({ password }) => {
    const checks = [
        password.length >= 8,
        /[A-Z]/.test(password),
        /[0-9]/.test(password),
        /[^A-Za-z0-9]/.test(password)
    ];
    const score = checks.filter(Boolean).length;
    const labels = ["", "Weak", "Fair", "Good", "Strong"];
    const colors = ["", "#ef4444", "#f59e0b", "#3b82f6", "#10b981"];

    return (
        <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                {[1,2,3,4].map(i => (
                    <div key={i} style={{
                        flex: 1, height: 4, borderRadius: 4,
                        background: i <= score ? colors[score] : "#e2e8f0",
                        transition: "background 0.3s"
                    }} />
                ))}
            </div>
            <p style={{ margin: 0, fontSize: 12, color: colors[score], fontWeight: 600 }}>
                {score > 0 && `Password strength: ${labels[score]}`}
            </p>
        </div>
    );
};

export default MyProfile;